import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useGenErrorMsgBanners } from "../useGenErrorMsgBanners";

/**
 * GET /api/aiAnnotation/baseTaxonomy.
 *
 * Server-side code module (no longer a per-tenant platformConfigs entry) that
 * serves the base annotation taxonomy:
 *   {
 *     thematics: [{ title, description, allowNewTags, tags: [{ title, description, category }] }],
 *     attributeCategories: [{ key, display, description, selectMode, enabled, tags: [{ display, description }] }]
 *   }
 *
 * Requires auth; axios baseURL already targets /api.
 */
const getBaseTaxonomy = async () => {
  const response = await axios.get("/api/aiAnnotation/baseTaxonomy");
  return response.data;
};

export const useGetBaseTaxonomy = () =>
  useQuery({
    queryKey: ["baseTaxonomy"],
    queryFn: getBaseTaxonomy,
  });

/**
 * GET /api/aiAnnotation/promptOverridesManifest.
 *
 * Server-generated manifest of overridable split-mode prompt blocks. Each
 * entry describes one block's tab assignment, display name, description,
 * LangChain placeholders (which MUST be preserved in an override), and
 * defaultContent (the in-source default the tenant can start from).
 *
 * Used by the PlatformConfigEditor's "Prompts" tab to render one Lexical
 * editor per block. See ai/dualAgentAnnotation/agents/annotator/prompts/
 * splitPromptOverrides.js on the BE for the source of truth.
 */
const getPromptOverridesManifest = async () => {
  const response = await axios.get("/api/aiAnnotation/promptOverridesManifest");
  return response.data;
};

export const useGetPromptOverridesManifest = () =>
  useQuery({
    queryKey: ["promptOverridesManifest"],
    queryFn: getPromptOverridesManifest,
    staleTime: Infinity, // manifest is code-generated, never changes at runtime
  });

/**
 * GET /api/aiAnnotation/getTopicClusteringTree?topicClusteringLens=<lens>.
 *
 * The tenant's observation-clustering tree (split-mode): bucket nodes →
 * finding leaves, each node { nodeId, label, description, annotationIds,
 * observations, ... }, plus unplacedObservations + signalMetaById. Returns
 * null (204-shaped) when no recluster has run yet for the lens.
 *
 * Shared by the Topics tab (TopicClusteringTreeView) and the Topics filter
 * (TopicClusterFilter) — one cache entry per lens.
 */
const getTopicClusteringTree = async (topicClusteringLens) => {
  // Omitting the query param entirely tells the BE to auto-pick the
  // most-recently-updated lens for the tenant.
  const url = topicClusteringLens
    ? `/api/aiAnnotation/getTopicClusteringTree?topicClusteringLens=${encodeURIComponent(topicClusteringLens)}`
    : `/api/aiAnnotation/getTopicClusteringTree`;
  const response = await axios.get(url);
  return response.data;
};

// Omit topicClusteringLens to get the tenant's configured lens (ootils
// resolveTopicClusteringLens on the BE) — formerly the most-recently-updated
// tree (the BE auto-picks) — useful when the caller doesn't know the
// lens vocabulary. Pass a specific name to pin to that lens.
export const useGetTopicClusteringTree = ({ topicClusteringLens, enabled = true } = {}) =>
  useQuery({
    queryKey: ["topicClusteringTree", topicClusteringLens || "__auto__"],
    queryFn: () => getTopicClusteringTree(topicClusteringLens),
    enabled,
    refetchOnWindowFocus: false,
    retry: 0,
  });

/**
 * POST /api/aiAnnotation/getTopicClusteringTreeCounts { topicClusteringLens, activeFilters }.
 *
 * The tree as COUNTS within a filter scope — per node annotationCount,
 * signalCategoryCounts, datasets, majority signalId (sub-topics), topics'
 * parked counts, unplacedCount, and `scope` chip counts — with the filters
 * applied on the server (processActiveFilters: datesThatMatterType for the
 * period, valuePathType for dataset/voice, the annotation tags filter for
 * signals). No observation arrays; ~100KB where the full tree is ~30MB on a
 * large tenant. Used by the Topics V2 tab. Null when no recluster has run.
 */
const getTopicClusteringTreeCounts = ({ topicClusteringLens, activeFilters, withPriorityStats }) =>
  axios
    .post("/api/aiAnnotation/getTopicClusteringTreeCounts", { topicClusteringLens, activeFilters, withPriorityStats })
    .then((res) => res.data);

// `withPriorityStats` adds docs + voice per node (the Priorities field's
// severity inputs) — its own cache entry, so the cards' request is untouched.
export const useGetTopicClusteringTreeCounts = ({ topicClusteringLens, activeFilters = [], withPriorityStats = false, enabled = true } = {}) =>
  useQuery({
    queryKey: ["topicClusteringTreeCounts", topicClusteringLens || "__auto__", activeFilters, withPriorityStats],
    queryFn: () => getTopicClusteringTreeCounts({ topicClusteringLens, activeFilters, withPriorityStats }),
    enabled,
    refetchOnWindowFocus: false,
    retry: 0,
    // A scope already counted this session is served from cache and NOT
    // re-fetched in the background — the key is the filter set, so flipping
    // back to an earlier selection is instant.
    staleTime: 5 * 60 * 1000,
  });

/**
 * POST /api/aiAnnotation/AIAutoAnnotate.
 *
 * Accepted variables:
 *   contentType          (required)
 *   docIds               (optional) — annotate only these docs; omit to scope to the
 *                                     whole contentType
 *   valuePaths           (optional) — annotate only these fields; omit for all
 *                                     annotation-enabled fields on the tpl
 *   skipExisting         (default true) — skip docs/fields already annotated
 *   showLogs             (default false) — verbose response (used by playground)
 *   allowedSignalTagIds  (optional, staging/dev only) — Signals-picker
 *                                     allow-list; when present, annotator only
 *                                     considers these Signal tag _ids +
 *                                     inductive Signal creation is suppressed.
 *                                     BE returns 400 if this arrives on prod
 *                                     (feature: aiAnnotateSignalPicker).
 *   annotatorCall1Model  (optional, staging/dev only) — model spec for this
 *                                     run's Call 1 ({ provider, modelName,
 *                                     baseURL?, apiKeyEnv?, maxDocsPerGroup? });
 *                                     the tenant's configured model is not
 *                                     changed. Same prod gate as above.
 *
 * No banner gets popped automatically except on error — callers handle their
 * own success UX (banner / floating progress / etc.).
 */
export const useTriggerAIAutoAnnotate = ({ silenceErrorBanner = false } = {}) => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();

  return useMutation(
    ({
      contentType,
      docIds,
      valuePaths,
      skipExisting = true,
      showLogs = false,
      allowedSignalTagIds,
      annotatorCall1Model,
    }) =>
      axios.post("/api/aiAnnotation/AIAutoAnnotate", {
        contentType,
        ...(Array.isArray(docIds) && docIds.length > 0 ? { docIds } : {}),
        ...(Array.isArray(valuePaths) && valuePaths.length > 0
          ? { valuePaths }
          : {}),
        skipExisting,
        showLogs,
        ...(Array.isArray(allowedSignalTagIds) && allowedSignalTagIds.length > 0
          ? { allowedSignalTagIds }
          : {}),
        ...(annotatorCall1Model ? { annotatorCall1Model } : {}),
      }),
    {
      onError: (err) => {
        if (!silenceErrorBanner) genErrorMsgBanners({ err });
      },
    }
  );
};

/**
 * POST /api/content/wipeDocumentAnnotations/:contentType/:contentId.
 *
 * Wipes ALL annotations (human + AI) from a single published doc. The backend
 * strips annoData + Lexical marks off the doc and saves it through the normal
 * quickUpdate pipeline, which propagates the deletes to the standalone
 * annotations collection + Elasticsearch.
 *
 * Variables: { contentType, contentId }.
 *
 * Like useTriggerAIAutoAnnotate, no success banner is popped automatically —
 * callers handle their own success UX + cache invalidation.
 */
export const useWipeDocumentAnnotations = ({ silenceErrorBanner = false } = {}) => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();

  return useMutation(
    ({ contentType, contentId }) =>
      axios.post(
        `/api/content/wipeDocumentAnnotations/${contentType}/${contentId}`,
      ),
    {
      onError: (err) => {
        if (!silenceErrorBanner) genErrorMsgBanners({ err });
      },
    }
  );
};

/**
 * Runtime logs (SYSTEM_AIRuntimeLogs), any type.
 *
 * With `type`: GET /api/aiRuntimeLogs?type=<type> — every run of that type
 * (comma-separate for several), newest first, WITHOUT step logs (list
 * projection).
 * With `id`:   GET /api/aiRuntimeLogs/:id — that one run WITH its step logs
 * (e.g. the reconciliation audit trail the topic-clustering detail
 * expander renders).
 */
const getRuntimeLogs = async ({ type, id }) => {
  const url = id ? `/api/aiRuntimeLogs/${id}` : `/api/aiRuntimeLogs?type=${encodeURIComponent(type)}`;
  const response = await axios.get(url);
  return response.data;
};

export const useGetRuntimeLogs = ({ type, id } = {}) => {
  const wantsDetail = !type;
  return useQuery({
    queryKey: ["runtimeLogs", wantsDetail ? `detail:${id}` : `list:${type}`],
    queryFn: () => getRuntimeLogs({ type, id }),
    // Detail mode with no id yet (nothing expanded) must not fire a request.
    enabled: !wantsDetail || !!id,
    refetchOnWindowFocus: false,
  });
};

/**
 * POST /api/aiAnnotation/setTopicClusteringStability — the human "lock
 * topics" action. stable: true freezes the structure (new observations get
 * placed incrementally, the tree stops reorganizing); false unlocks (full
 * reclusters resume). Invalidates the tree readers — the full tree and the
 * counts payload (which carries `stable` too) — so the footer state and any
 * lock UI refresh immediately.
 */
export const useSetTopicClusteringStability = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ topicClusteringLens, stable }) =>
      axios.post("/api/aiAnnotation/setTopicClusteringStability", { topicClusteringLens, stable }),
    {
      onSuccess: (_res, { topicClusteringLens }) => {
        queryClient.invalidateQueries(["topicClusteringTree", topicClusteringLens]);
        queryClient.invalidateQueries(["topicClusteringTreeCounts"]);
      },
    }
  );
};

/**
 * POST /api/aiAnnotation/reclusterTopics { mode, force } — run a clustering
 * mode's lens NOW (zero-delay job, no 10-minute quiescence wait; `force`
 * runs even on an unchanged corpus). Off-prod tooling: the BE rejects
 * mode/force on prod (topicsReclusterControl). The job runs on okf-sub;
 * the tree readers are refreshed when the user next loads, not here.
 */
export const useReclusterTopics = () =>
  useMutation(({ mode, force = true }) => axios.post("/api/aiAnnotation/reclusterTopics", { mode, force }));

/**
 * POST /api/aiAnnotation/narratePriorityInsights { facts, periodKey }.
 *
 * Phrases the Priorities view's computed insights for a PM: an opener for
 * the period plus { id, headline, why, check, valid } per insight. The BE
 * caches by a hash of the facts, so the same facts never reach the model
 * twice; `factsKey` is the FE's cheap hash of the same facts, used only as
 * the react-query key. Insights the BE could not validate come back with
 * valid:false and the caller keeps their template wording.
 */
const narratePriorityInsights = async ({ facts, periodKey }) => {
  const response = await axios.post("/api/aiAnnotation/narratePriorityInsights", { facts, periodKey });
  return response.data;
};

export const useNarratePriorityInsights = ({ facts, factsKey, periodKey, enabled = true } = {}) =>
  useQuery({
    queryKey: ["priorityInsightsNarration", factsKey],
    queryFn: () => narratePriorityInsights({ facts, periodKey }),
    enabled: enabled && !!factsKey,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 0,
  });
